import { z } from "zod";

// AspectRatio enum
const aspectRatioSchema = z.enum(["9:16", "16:9"]);
export type AspectRatio = z.infer<typeof aspectRatioSchema>;

// Client enum
const clientSchema = z.enum(["DESKTOP", "IOS", "ANDROID"]);

// VideoType enum - matches proto enum definitions
const videoTypeSchema = z.enum([
  "VIDEO_TYPE_UNSPECIFIED",
  "VIDEO_TYPE_TALKING_HEAD",
  "VIDEO_TYPE_VOICEOVER",
  "VIDEO_TYPE_VOICEOVER_MIRAGE",
  "VIDEO_TYPE_TALKING_HEAD_MIRAGE",
  "VIDEO_TYPE_REAL_MIRAGE",
]);
export type VideoType = z.infer<typeof videoTypeSchema>;

// Accept both proto-enum integers (legacy cap-srv encoding) and string
// variants ("TALKING_HEAD", "CATEGORY_AROLL", ...). The Oasis
// /oasis/ai-edit/shot-styles response now emits string variants so jerboa
// can decode them, but a defensive parser is cheap and means the form
// keeps working if any legacy callsite returns ints. The form normalizes
// either form to a string via `shotTypeFromProto` / `displayCategoryFromProto`
// in `~/modules/ai-edit-tool/constants`.
const shotTypeSchema = z.union([z.number(), z.string()]);

const shotStyleCategorySchema = z.union([z.number(), z.string()]);

// Color schema (decimal format 0-1)
const colorSchema = z.object({
  red: z.number().optional(),
  green: z.number().optional(),
  blue: z.number().optional(),
  alpha: z.number().optional(),
});
export type Color = z.infer<typeof colorSchema>;

// StrokeStyle in Oasis stores only variant + width; color is resolved at
// render time from the project's selected color (fallback white).
//
// `variant` is the StrokeStyleVariant proto enum. The /oasis/ai-edit/shot-styles
// response now emits enums as string variants ("STROKE_STYLE_VARIANT_CHALK");
// older callers send the wire integer (0 = unset, 1 = chalk). Accept both and
// normalize to the integer the form's `=== 1` check, the upsert write path, and
// the smart-video renderer all expect — without this, string-encoded chalk
// styles render as "no stroke" and get wiped on save.
const strokeStyleVariantByName: Record<string, number> = {
  STROKE_STYLE_VARIANT_UNSPECIFIED: 0,
  STROKE_STYLE_VARIANT_CHALK: 1,
};
export const strokeStyleSchema = z.object({
  variant: z
    .union([z.number(), z.string()])
    .transform((value) =>
      typeof value === "number" ? value : (strokeStyleVariantByName[value] ?? 0)
    ),
  width: z.number().optional(),
});
export type StrokeStyle = z.infer<typeof strokeStyleSchema>;

// ClientConfig schema
const clientConfigSchema = z.union([
  z.object({
    inDev: z.boolean(),
  }),
  z.object({
    minSupportedVersion: z.string(),
  }),
  z.object({}),
]);
export type ClientConfig = z.infer<typeof clientConfigSchema>;

// Template schema. cap-srv emits the full caption-template proto body
// (colors, style.content.custom, emojiSettings, etc) — passthrough so the
// preview can apply it via jerboa's applyCaptionTemplate action.
const templateSchema = z
  .object({
    id: z.string().optional(),
    displayName: z.string().optional(),
  })
  .passthrough();

// CaptionsColorOverrideConfig schema
//
// `colorOverrideTarget` / `colorOverrideLevel` are proto enums. Like the
// stroke/shot-type enums above, the /oasis/ai-edit/shot-styles response emits
// them as string variants ("COLOR_OVERRIDE_TARGET_PRIMARY"); older callers send
// the wire integer (1 = primary, 2 = background, ...). Accept both and normalize
// to the integer the picker's number-keyed lookup and the upsert write path
// expect — without this, string-encoded targets render as an empty badge and
// round-trip back to the server as strings on save.
export const colorOverrideTargetByName: Record<string, number> = {
  COLOR_OVERRIDE_TARGET_UNSPECIFIED: 0,
  COLOR_OVERRIDE_TARGET_PRIMARY: 1,
  COLOR_OVERRIDE_TARGET_BACKGROUND: 2,
  COLOR_OVERRIDE_TARGET_EMPHASIS: 3,
  COLOR_OVERRIDE_TARGET_ACTIVE: 4,
  COLOR_OVERRIDE_TARGET_ACTIVE_WORD_BACKGROUND: 5,
  COLOR_OVERRIDE_TARGET_SHADOW: 6,
  COLOR_OVERRIDE_TARGET_BACKGROUND_STROKE: 7,
};
export const colorOverrideLevelByName: Record<string, number> = {
  COLOR_OVERRIDE_LEVEL_UNSPECIFIED: 0,
  COLOR_OVERRIDE_LEVEL_PRIMARY: 1,
  COLOR_OVERRIDE_LEVEL_SECONDARY: 2,
  COLOR_OVERRIDE_LEVEL_TERTIARY: 3,
};
const captionsColorOverrideConfigSchema = z.object({
  colorOverrideTarget: z // 1=primary, 2=background, 3=emphasis, 4=active, 5=active word background, 6=shadow, 7=background stroke
    .union([z.number(), z.string()])
    .transform((value) =>
      typeof value === "number" ? value : (colorOverrideTargetByName[value] ?? 0)
    ),
  colorOverrideLevel: z // 1=primary, 2=secondary, 3=tertiary
    .union([z.number(), z.string()])
    .transform((value) =>
      typeof value === "number" ? value : (colorOverrideLevelByName[value] ?? 0)
    ),
});
export type CaptionsColorOverrideConfig = z.infer<typeof captionsColorOverrideConfigSchema>;

// AiEditThumbnail schema (simplified)
const aiEditThumbnailSchema = z.object({}).passthrough();

export const timestampSchema = z
  .preprocess((value) => {
    if (value == null) {
      return null;
    }
    if (typeof value === "string") {
      return value;
    }
    if (typeof value === "number") {
      const millis = value > 1e12 ? value : value * 1000;
      const date = new Date(millis);
      return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }
    if (typeof value === "object") {
      const obj = value as Record<string, unknown>;
      const seconds = obj._seconds ?? obj.seconds;
      const nanos = obj._nanoseconds ?? obj.nanoseconds ?? obj.nanos ?? 0;
      if (typeof seconds === "number" && typeof nanos === "number") {
        const date = new Date(seconds * 1000 + nanos / 1e6);
        return Number.isNaN(date.getTime()) ? null : date.toISOString();
      }
    }
    return null;
  }, z.string().nullish())
  .transform((value) => value ?? undefined);

// Per-AR preview slot URLs (Stage 5b.1). The server emits this for each
// AR the style supports + that has a `previews.{ar}` entry on the doc.
// Optimized variants are only included for codecs that have been
// generated; absence = "not yet optimized for that codec".
//
// Use .nullish() (not .optional()) on every field — cap-srv response
// schemas can send explicit `null` for absent fields after the proto
// round-trip + Zod re-serialization on the server; .optional() would
// reject those. .nullish() accepts both null and undefined.
export const previewSlotUrlSchema = z.object({
  previewVideoUrl: z.string(),
  thumbnailUrl: z.string(),
  optimizedVariants: z
    .object({
      av1: z.string().nullish(),
      webm: z.string().nullish(),
    })
    .nullish(),
});
export type PreviewSlotUrl = z.infer<typeof previewSlotUrlSchema>;

export const previewsUrlMapSchema = z.object({
  vertical: previewSlotUrlSchema.nullish(),
  horizontal: previewSlotUrlSchema.nullish(),
});
export type PreviewsUrlMap = z.infer<typeof previewsUrlMapSchema>;

// Style (VideoStyleTemplate) schema. Matches the Oasis v2 response shape
// after Stage 6.5c — per-AR `previews` + `display*` are the canonical
// URL surface; legacy aliases (previewUrl/thumbnailUrl/hlsPreviewUrl/
// captionsPreviewUrl/captionsThumbnailUrl) are stripped server-side.
//
// `previews` + `displayPreviewVideoUrl` + `displayThumbnailUrl` use
// `.nullable()` to tolerate cap-srv emitting explicit null after the
// proto round-trip — even though the server's own Zod requires them
// non-null, defense-in-depth matches the codebase pattern (see
// `previewSlotUrlSchema` above and the related fields elsewhere).
const styleSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  tags: z.array(z.string()),
  ios: clientConfigSchema.optional(),
  desktop: clientConfigSchema.optional(),
  android: clientConfigSchema.optional(),
  colorPresets: z.array(colorSchema).optional().default([]),
  captionsTemplate: templateSchema.optional(),
  captionsColorOverrideConfigs: z.array(captionsColorOverrideConfigSchema).default([]),
  thumbnails: z.array(aiEditThumbnailSchema).default([]),
  supportedAspectRatios: z.array(aspectRatioSchema).optional(),
  previews: previewsUrlMapSchema.nullable(),
  displayPreviewVideoUrl: z.string().nullable(),
  displayThumbnailUrl: z.string().nullable(),
  updatedOn: timestampSchema,
});
export type Style = z.infer<typeof styleSchema>;

// ShotStyleTemplate metadata schema
const shotStyleMetadataSchema = z.object({
  shotType: shotTypeSchema.optional(),
  ios: clientConfigSchema.optional(),
  desktop: clientConfigSchema.optional(),
  android: clientConfigSchema.optional(),
  thumbnailUri: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  supportedVideoTypes: z.array(videoTypeSchema).optional(),
  category: shotStyleCategorySchema.optional(),
});

// ShotStyleTemplate schema (simplified - adjust based on actual API response)
const shotStyleTemplateSchema = z
  .object({
    id: z.string(),
    displayName: z.string(),
    metadata: shotStyleMetadataSchema.optional(),
    strokeStyle: strokeStyleSchema.optional(),
  })
  .passthrough();
export type ShotStyleTemplate = z.infer<typeof shotStyleTemplateSchema>;

// Paginated response schema
const pageInfoSchema = z.object({
  hasNextPage: z.boolean(),
  endCursor: z.string(),
});

// GetAiEditStylesRequest schema
export const getAiEditStylesRequestSchema = z.object({
  client: clientSchema,
  version: z.string(),
  afterCursor: z.string(),
  tags: z.array(z.string()),
  pageSize: z.number(),
});

// GetAiEditStylesResponse schema
export const getAiEditStylesResponseSchema = z.object({
  styles: z.object({
    page: z.array(styleSchema),
    pageInfo: pageInfoSchema,
  }),
});

// GetShotStylesRequest schema
export const getShotStylesRequestSchema = z.object({
  videoStyleId: z.string(),
});

// GetShotStylesResponse schema
export const getShotStylesResponseSchema = z.object({
  shotTemplates: z.array(shotStyleTemplateSchema),
  defaultShotStyleId: z.string().optional(),
});
export type GetShotStylesResponse = z.infer<typeof getShotStylesResponseSchema>;

const captionsTemplateItemSchema = z.object({
  id: z.string(),
  displayName: z.string().optional(),
});

export const getCaptionsTemplatesResponseSchema = z.object({
  templates: z.array(captionsTemplateItemSchema),
});

// Per-AR preview slot — URI form (matches cap-srv's PreviewsMap proto).
// Distinct from previewSlotUrlSchema above which is URL form, emitted on
// responses. Stage 5b.2: clients send these on upsert to update per-AR
// slots; cap-srv mirrors the primary AR's slot to the legacy top-level
// previewUri/thumbnailUri for back-compat with non-per-AR-aware callers.
const previewSlotUriSchema = z.object({
  previewUri: z.string(),
  thumbnailUri: z.string(),
  optimizedVariants: z
    .object({
      av1: z.string().optional(),
      webm: z.string().optional(),
    })
    .optional(),
});

const previewsUriMapSchema = z.object({
  vertical: previewSlotUriSchema.optional(),
  horizontal: previewSlotUriSchema.optional(),
});

// UpsertAiEditStyleRequest schema (for updates)
export const upsertAiEditStyleRequestSchema = z.object({
  id: z.string(),
  tags: z.array(z.string()).optional(),
  ios: clientConfigSchema.optional(),
  desktop: clientConfigSchema.optional(),
  android: clientConfigSchema.optional(),
  supportedAspectRatios: z.array(aspectRatioSchema).optional(),
  previewUri: z.string().optional(),
  thumbnailUri: z.string().optional(),
  previews: previewsUriMapSchema.optional(),
  videoTemplate: z
    .object({
      id: z.string(),
      displayName: z.string(),
      captionsTemplate: z
        .object({
          id: z.string(),
          displayName: z.string().optional(),
        })
        .optional(),
      captionsColorOverrideConfigs: z.array(captionsColorOverrideConfigSchema),
    })
    .optional(),
  colorPresets: z.array(colorSchema).optional(),
});

// CreateAiEditStyleRequest schema (for creating new styles)
export const createAiEditStyleRequestSchema = z.object({
  id: z.string(),
  videoTemplate: z.object({
    id: z.string(),
    displayName: z.string(),
    captionsTemplate: z
      .object({
        id: z.string(),
        displayName: z.string().optional(),
      })
      .optional(),
    captionsColorOverrideConfigs: z.array(captionsColorOverrideConfigSchema),
  }),
  shotTemplates: z.array(z.record(z.unknown())),
  previewUri: z.string().optional(),
  tags: z.array(z.string()),
  ios: clientConfigSchema.optional(),
  desktop: clientConfigSchema.optional(),
  android: clientConfigSchema.optional(),
  thumbnailUri: z.string().optional(),
  colorPresets: z.array(colorSchema),
  supportedAspectRatios: z.array(aspectRatioSchema).optional(),
});

// UpsertAiEditStyleResponse schema
export const upsertAiEditStyleResponseSchema = z.object({
  style: styleSchema,
});

export const optimizeAiEditStylePreviewRequestSchema = z.object({
  videoStyleId: z.string(),
  previewUri: z.string(),
});

const optimizeAiEditStylePreviewFormatResultSchema = z.object({
  status: z.enum(["completed", "failed"]),
  objectPath: z.string(),
  error: z.string().optional(),
});

export const optimizeAiEditStylePreviewResponseSchema = z.object({
  status: z.enum(["completed", "partial_failure", "failed"]),
  formats: z.object({
    h264: optimizeAiEditStylePreviewFormatResultSchema.optional(),
    av1: optimizeAiEditStylePreviewFormatResultSchema,
    webm: optimizeAiEditStylePreviewFormatResultSchema,
  }),
});
export type OptimizeAiEditStylePreviewResponse = z.infer<
  typeof optimizeAiEditStylePreviewResponseSchema
>;

// UpsertShotStyleTemplateRequest schema
export const upsertShotStyleTemplateRequestSchema = z.object({
  id: z.string(),
  shotTemplate: z.record(z.unknown()),
});

// UpsertShotStyleTemplateResponse schema
export const upsertShotStyleTemplateResponseSchema = z.object({
  id: z.string(),
  shotTemplate: shotStyleTemplateSchema.optional(),
});

// AddShotStyleToVideoStyleRequest schema
export const addShotStyleToVideoStyleRequestSchema = z.object({
  videoStyleId: z.string(),
  shotStyleId: z.string(),
  isDefaultShotStyle: z.boolean(),
});

// AddShotStyleToVideoStyleResponse schema
export const addShotStyleToVideoStyleResponseSchema = z.object({
  videoStyleId: z.string(),
  shotStyleId: z.string(),
  isDefaultShotStyle: z.boolean(),
});

// GetEditableStylesResponse schema
export const getEditableStylesResponseSchema = z.object({
  editableStyleIds: z.array(z.string()),
});

// SyncCaptionsTemplates schemas
export const syncCaptionsTemplatesRequestSchema = z.object({
  templateIds: z.array(z.string()).min(1).max(50),
});

const syncCaptionsTemplateResultSchema = z.object({
  templateId: z.string(),
  displayName: z.string().optional(),
  status: z.enum(["synced", "skipped", "error"]),
  error: z.string().optional(),
});

export const syncCaptionsTemplatesResponseSchema = z.object({
  results: z.array(syncCaptionsTemplateResultSchema),
});
